import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { servicesAPI } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Loader2, Tag } from "lucide-react";

export default function AdminServiceCategories() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState("");
  const [adding, setAdding] = useState(false);

  const isAdmin = user?.is_admin || user?.is_super_admin;

  const load = () => {
    servicesAPI.adminListCategories()
      .then((r) => setCats(r.data?.categories || []))
      .catch(() => toast.error("Could not load categories"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin]);

  const add = async () => {
    if (!label.trim()) return;
    setAdding(true);
    try {
      await servicesAPI.adminCreateCategory({ label: label.trim(), sort_order: cats.length + 1 });
      setLabel("");
      load();
      toast.success("Category added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    try { await servicesAPI.adminDeleteCategory(id); load(); toast.success("Removed"); }
    catch { toast.error("Could not remove"); }
  };

  if (!isAdmin) return <div className="py-20 text-center text-muted-foreground">Admin access required.</div>;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <h1 className="flex items-center gap-2 font-display text-3xl font-bold"><Tag className="h-6 w-6 text-primary" /> Service categories</h1>

      <Card className="space-y-4 border-border bg-card p-5">
        <div className="flex gap-2">
          <Input value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="New category name" data-testid="admin-service-cat-input" />
          <Button onClick={add} disabled={adding} className="bg-primary text-primary-foreground" data-testid="admin-service-cat-add">
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </div>
        {loading ? (
          <Loader2 className="mx-auto h-5 w-5 animate-spin" />
        ) : (
          <ul className="divide-y divide-border" data-testid="admin-service-cat-list">
            {cats.map((c) => (
              <li key={c.id} className="flex items-center justify-between py-2.5">
                <span className={`text-sm ${c.is_active ? "" : "text-muted-foreground line-through"}`}>{c.label}</span>
                {c.is_active && (
                  <button onClick={() => remove(c.id)} className="text-muted-foreground hover:text-destructive" data-testid={`admin-service-cat-delete-${c.slug}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
